import React from 'react';
import styled from "styled-components";
import { Button } from "primereact/button";
import { formulas } from "../shared/config.formulas";
import { compact, last, values } from 'lodash';
import { useNavigate } from "react-router-dom";
import { SubscriptionSubType } from 'shared/types/types';
import { useQuery } from "@apollo/react-hooks";
import { ProductByCodeQuery } from "../pages/SubscriptionWizard/SubscriptionWizard.gql";
import { ProductByCode } from "../__generated__/types";
import { formatCurrency } from "../../src/shared/utils/currency.utils";
import { vat } from "../../src/shared/utils/vat.utils";

const Formulas = styled.div<{ columns: number }>`
  display: grid;
  grid-gap: 16px;

  @media only screen and (min-width: 1000px) {
    grid-template-columns: repeat(${({columns}) => columns}, 1fr);
  }
`;

const Formula = styled.div`
  background: linear-gradient(135deg, rgba(116, 143, 86, 0.10) 0%, rgba(238, 122, 49, 0.08) 100%);
  border: 1px solid var(--surface-200);
  border-radius: 1rem;
  box-shadow: var(--shadow-2);
  overflow: hidden;

  display: grid;
  grid-template-rows: max-content 1fr max-content max-content;

  .title {
    background: var(--orange);
    padding: 24px 16px;
    text-align: center;

    h2 {
      margin: 0 0 8px 0;
      color: white;
      text-transform: uppercase;
      font-size: 2rem;
      line-height: 1;
      font-weight: 700;
    }

    h3 {
      margin: 0;
      color: white;
      font-size: 1.15rem;
      line-height: 1.4;
      font-weight: normal;
    }
  }

  &.highlight {
    border-width: 1px;
    border-color: var(--surface-200);

    .title {
      background: var(--orange);

      h2 {
        font-weight: bold;
      }
    }
  }
`;

const Description = styled.div`
  padding: 24px 24px 8px 24px;

  ul {
    margin: 0;
    padding-left: 20px;
    font-size: 1.05rem;
    line-height: 1.7;
  }

  li::marker {
    color: var(--orange);
  }
`;

const Pricing = styled.div<{ options: number }>`
  padding: 16px 24px 8px 24px;

  h3 {
    margin: 0 0 12px 0;
    text-align: center;
    color: var(--green);
    font-size: 1.5rem;
  }

  .prices {
    display: grid;
    grid-template-columns: repeat(${props => props.options}, 1fr);
    grid-gap: 8px;

    > div {
      padding: 16px;
      border-radius: 1rem;
      background: rgba(255, 255, 255, 0.75);
      text-align: center;
      display: grid;
      grid-template-rows: max-content max-content;
      align-content: center;

      span.name {
        color: var(--green);
      }

      span.name,
      span.price {
        font-size: 1.1em;
        font-weight: bold;
      }

      span.price {
        color: var(--orange);
        font-size: 1.6em;
        line-height: 1.2;
      }
    }
  }

  .highlight > & {
    .prices > div {
      span.name {
        color: var(--orange);
      }
    }
  }
`;

const Order = styled.div`
  padding: 16px 24px 24px 24px;
  display: grid;
  grid-gap: 8px;
  grid-template-rows: max-content max-content;
  text-align: center;

  .p-button {
    justify-content: center;
  }

  .p-button.p-button-outlined {
    color: white;
    background: var(--orange);
    border-color: var(--orange);
    font-weight: bold;
  }

  .p-button.p-button-outlined:hover {
    color: white;
    background: var(--orange);
    border-color: var(--orange);
    filter: brightness(0.95);
  }

  .p-button.p-button-link {
    color: var(--surface-600);
    font-size: 0.9em;
  }
`;

const SelfHarvestFormula = () => {
  const {data} = useQuery<ProductByCode>(ProductByCodeQuery, {variables: {code: 'zelfoogst-2023'}});
  const navigate = useNavigate();
  let filteredFormulas = values(formulas)
    .filter(f => [SubscriptionSubType.SELF_HARVEST].includes(f.membershipSubType));
  return <div>
    <Formulas columns={filteredFormulas.length}>
      {filteredFormulas
        .map(formula => {
          let product = data?.product;
          let priceExcl = last(compact(product?.productPrices))?.value;
          let price = (product && priceExcl) && priceExcl * (1 + vat(product.vat));
          return (
            <Formula
              className={'highlight'}
              key={formula.membershipSubType}>
              <div className='title'>
                <h2>{formula.name}</h2>
                <h3>{formula.shortDescription}</h3>
              </div>

              <Description>
                <ul>
                  {formula.description?.map((desc, idx) => <li key={`desc${idx}`}>{desc}</li>)}
                </ul>
              </Description>
              <Pricing options={formula.pricing.length}>
                <h3>{formula.pricing.length === 1 ? 'Tarief' : 'Tarieven'}</h3>
                <div className="prices">

                  {formula.pricing.map(pricing => <div key={pricing.title}>
                    {formula.pricing.length > 1 && <span className={'name'}>{pricing.title}</span>}
                    <span className={'price'}>{price && formatCurrency(price, 0)},- per persoon</span>
                  </div>)}
                </div>
              </Pricing>
              <Order>
                <Button
                  label={'Word nu zelfoogster'}
                  icon={'pi pi-arrow-right'}
                  iconPos={'right'}
                  className="p-button-outlined p-button-lg"
                  onClick={() => {
                    window.location.href = `https://www.mijnboer.be/groentegeweld/inschrijven/zelfoogst`;
                  }}
                />
                <Button
                  label={'probeer 8 weken'}
                  className="p-button-link"
                  onClick={() => {
                    window.location.href = `https://www.mijnboer.be/groentegeweld/inschrijven/zelfoogst`;
                  }}
                />
              </Order>
            </Formula>
          );
        })}
    </Formulas>

  </div>;
};

export default SelfHarvestFormula;
